import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  buildPosDescriptionPreview,
  getEffectivePosDescriptionFields,
  getPosDescriptionConfig,
  sanitizePosDescriptionFields,
} from '@/lib/productPosDescription';

export const ProductPosDescriptionConfigurator = ({
  category,
  catalogRule,
  value,
  onChange,
  brand,
  model,
  specs,
  testIdPrefix = 'product',
  readOnly = false,
}) => {
  const config = catalogRule?.config || getPosDescriptionConfig(category);
  const resolvedCatalogRule = catalogRule || {
    enabled: value?.catalog_pos_description_enabled !== false && Boolean(config),
    fields: value?.catalog_pos_description_fields || [],
    source: value?.catalog_pos_description_source || 'category',
    config,
  };

  const state = {
    ...(value || {}),
    pos_description_use_override: Boolean(value?.pos_description_use_override),
    pos_description_fields: sanitizePosDescriptionFields(category, value?.pos_description_fields || []),
    catalog_pos_description_enabled: resolvedCatalogRule.enabled,
    catalog_pos_description_fields: resolvedCatalogRule.fields || [],
    name: value?.name,
    brand,
    model,
    specs: value?.specs || value?.technical_specs || specs,
    technical_specs: value?.technical_specs || value?.specs || specs,
    category,
  };

  const effectiveFields = getEffectivePosDescriptionFields(category, state);
  const preview = buildPosDescriptionPreview(state, category);
  const hasRule = Boolean(config && resolvedCatalogRule?.enabled);
  const ruleLabel = resolvedCatalogRule?.source === 'subcategory'
    ? 'Default da subcategoria'
    : resolvedCatalogRule?.source === 'category'
      ? 'Default da categoria'
      : 'Default da categoria';

  const handleToggleField = (fieldKey, checked) => {
    const current = sanitizePosDescriptionFields(category, state.pos_description_fields);
    const next = checked
      ? [...current, fieldKey]
      : current.filter((field) => field !== fieldKey);

    onChange({
      pos_description_fields: next,
      pos_description_use_override: next.length > 0,
    });
  };

  return (
    <div className="space-y-3 rounded-lg border p-4" data-testid={`${testIdPrefix}-pos-description-section`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Descrição no POS</h3>
          <p className="text-xs text-muted-foreground">
            {hasRule
              ? 'Configuração por categoria com override opcional por produto.'
              : 'Sem regra de descrição POS configurada para esta categoria.'}
          </p>
        </div>

        {hasRule && !readOnly && (
          <div className="flex items-center gap-2">
            <Label htmlFor={`${testIdPrefix}-pos-description-override`} className="text-xs text-muted-foreground">
              Personalizar neste produto
            </Label>
            <Switch
              id={`${testIdPrefix}-pos-description-override`}
              checked={state.pos_description_use_override}
              onCheckedChange={(checked) => onChange({
                pos_description_use_override: checked,
                pos_description_fields: checked
                  ? (state.pos_description_fields.length > 0 ? state.pos_description_fields : config.defaultFields)
                  : [],
              })}
              data-testid={`${testIdPrefix}-pos-description-override-switch`}
            />
          </div>
        )}
      </div>

      {hasRule ? (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-pos-description-effective-fields`}>
            <Badge variant={state.pos_description_use_override ? 'default' : 'outline'} data-testid={`${testIdPrefix}-pos-description-origin-badge`}>
              {state.pos_description_use_override ? 'Override do produto' : ruleLabel}
            </Badge>
            {effectiveFields.map((fieldKey) => {
              const fieldMeta = config.fields.find((field) => field.key === fieldKey);
              return (
                <Badge key={`${testIdPrefix}-effective-${fieldKey}`} variant="secondary">
                  {fieldMeta?.label || fieldKey}
                </Badge>
              );
            })}
          </div>

          {state.pos_description_use_override && !readOnly && (
            <div className="grid gap-2 md:grid-cols-2" data-testid={`${testIdPrefix}-pos-description-checkbox-grid`}>
              {config.fields.map((field) => {
                const checked = state.pos_description_fields.includes(field.key);
                return (
                  <label
                    key={`${testIdPrefix}-${field.key}`}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                    data-testid={`${testIdPrefix}-pos-description-field-${field.key}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(next) => handleToggleField(field.key, Boolean(next))}
                    />
                    <span>{field.label}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="rounded-md bg-muted/50 p-3" data-testid={`${testIdPrefix}-pos-description-preview`}>
            <p className="text-xs font-medium text-muted-foreground">Pré-visualização</p>
            <p className="mt-1 text-sm">{preview || 'Sem descrição técnica configurada para o POS.'}</p>
          </div>
        </>
      ) : (
        <div className="rounded-md border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-pos-description-no-rule-message`}>
          Regra de descrição POS desactivada para esta categoria/subcategoria.
        </div>
      )}
    </div>
  );
};